import { useState, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Camera, X, Crown, GripVertical, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import API_URL from '../config/api';

function SortablePhoto({ photo, index, onDelete, onSetPrimary }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.photo_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative aspect-[3/4] rounded-2xl overflow-hidden bg-[#F5F2EB] group ${isDragging ? 'shadow-2xl ring-2 ring-[#6E1A24]' : 'shadow-sm'}`}
      data-testid={`photo-slot-${index}`}
    >
      <img
        src={`${API_URL}/api/photos/${photo.photo_id}?v=${Date.now()}`}
        alt=""
        className="w-full h-full object-cover"
      />

      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 w-8 h-8 rounded-lg bg-black/40 backdrop-blur-sm flex items-center justify-center text-white cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`photo-drag-${index}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* Primary badge */}
      {photo.is_primary && (
        <div className="absolute top-2 right-2 bg-[#6E1A24] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full flex items-center gap-1" data-testid={`photo-primary-badge-${index}`}>
          <Crown className="w-3 h-3" />
          Main
        </div>
      )}

      {/* Bottom actions */}
      <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-between">
        {!photo.is_primary && (
          <button
            onClick={() => onSetPrimary(photo.photo_id)}
            className="text-white text-[11px] font-medium bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 hover:bg-white/30 transition-colors"
            data-testid={`photo-set-main-${index}`}
          >
            Set as main
          </button>
        )}
        <button
          onClick={() => onDelete(photo.photo_id)}
          className="ml-auto w-7 h-7 rounded-full bg-red-500/80 flex items-center justify-center text-white hover:bg-red-600 transition-colors"
          data-testid={`photo-delete-${index}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function PhotoGrid({ photos, onPhotosChange }) {
  const [uploading, setUploading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 6) {
      toast.error('Maximum 6 photos');
      return;
    }
    setUploading(true);
    const newPhotos = [...photos];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API_URL}/api/photos/upload`, fd, { withCredentials: true });
        newPhotos.push({
          photo_id: res.data.photo_id,
          is_primary: newPhotos.length === 0,
        });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Upload failed');
      }
    }
    // If first photo, set as primary
    if (photos.length === 0 && newPhotos.length > 0) {
      try {
        await axios.post(`${API_URL}/api/photos/${newPhotos[0].photo_id}/set-primary`, {}, { withCredentials: true });
        newPhotos[0].is_primary = true;
      } catch (err) {
        // Error already shown via toast
      }
    }
    onPhotosChange(newPhotos);
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = useCallback(async (photoId) => {
    try {
      await axios.delete(`${API_URL}/api/photos/${photoId}`, { withCredentials: true });
      const updated = photos.filter(p => p.photo_id !== photoId);
      // If deleted was primary and others remain, set first as primary
      const deleted = photos.find(p => p.photo_id === photoId);
      if (deleted?.is_primary && updated.length > 0) {
        await axios.post(`${API_URL}/api/photos/${updated[0].photo_id}/set-primary`, {}, { withCredentials: true });
        updated[0].is_primary = true;
      }
      onPhotosChange(updated);
      toast.success('Photo removed');
    } catch (err) {
      toast.error('Failed to delete photo');
    }
  }, [photos, onPhotosChange]);

  const handleSetPrimary = useCallback(async (photoId) => {
    try {
      await axios.post(`${API_URL}/api/photos/${photoId}/set-primary`, {}, { withCredentials: true });
      const updated = photos.map(p => ({
        ...p,
        is_primary: p.photo_id === photoId,
      }));
      // Move primary to first
      const primary = updated.find(p => p.photo_id === photoId);
      const rest = updated.filter(p => p.photo_id !== photoId);
      onPhotosChange([primary, ...rest]);
      toast.success('Main photo updated');
    } catch (err) {
      toast.error('Failed to set main photo');
    }
  }, [photos, onPhotosChange]);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = photos.findIndex(p => p.photo_id === active.id);
    const newIndex = photos.findIndex(p => p.photo_id === over.id);
    const reordered = [...photos];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    onPhotosChange(reordered);

    try {
      await axios.post(`${API_URL}/api/photos/reorder`, {
        photo_ids: reordered.map(p => p.photo_id),
      }, { withCredentials: true });
      // After reorder, first photo becomes primary
      const refreshed = reordered.map((p, i) => ({ ...p, is_primary: i === 0 }));
      onPhotosChange(refreshed);
    } catch (err) {
      toast.error('Failed to save order');
    }
  }, [photos, onPhotosChange]);

  const emptySlots = Math.max(0, 6 - photos.length - (uploading ? 1 : 0));

  return (
    <div data-testid="photo-grid">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={photos.map(p => p.photo_id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-3 gap-2.5">
            {photos.map((photo, i) => (
              <SortablePhoto
                key={photo.photo_id}
                photo={photo}
                index={i}
                onDelete={handleDelete}
                onSetPrimary={handleSetPrimary}
              />
            ))}

            {/* Uploading indicator */}
            {uploading && (
              <div className="aspect-[3/4] rounded-2xl bg-[#F5F2EB] flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-[#6E1A24] animate-spin" />
              </div>
            )}

            {/* Empty slots */}
            {Array.from({ length: emptySlots }).map((_, i) => (
              <label
                key={`empty-${i}`}
                className="aspect-[3/4] rounded-2xl border-2 border-dashed border-[#E5E3DB] flex flex-col items-center justify-center cursor-pointer hover:border-[#6E1A24] hover:bg-[#6E1A24]/5 transition-all"
                data-testid={`photo-add-slot-${photos.length + i}`}
              >
                <Camera className="w-6 h-6 text-[#9C9C97] mb-1.5" />
                <span className="text-xs font-medium text-[#9C9C97]">Add Photo</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleUpload}
                  className="hidden"
                  multiple
                />
              </label>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <p className="text-xs text-[#9C9C97] mt-3 text-center">
        {photos.length}/6 photos &middot; Drag to reorder &middot; First photo is your main
      </p>
    </div>
  );
}
